"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import BigNumber from "bignumber.js";
import type { Environment } from "../../../types";
import { resolveEnvironment } from "../../../lib/environment";
import { parseNumeric } from "../../../lib/numeric";
import { cn } from "../../../lib/utils";
import { DEFAULT_ONEUSD_TERMS_URL } from "../../../lib/constants";
import { TrackingBoundary } from "../../../lib/sentry";
import { useSoftKeyboardOpen } from "../../../hooks/use-soft-keyboard-open";
import {
  resolveEnvValue,
  useRemoteConfigValue,
  type AppsConfig,
} from "../../../lib/remote-config";
import { useExitDelay } from "../../../hooks/use-exit-delay";
import { BackIcon, ChevronDownIcon, CloseIcon } from "../../wallet-info/components/icons";
import {
  ArrowDownIcon,
  ArrowRightIcon,
  HistoryIcon,
  SlidersIcon,
} from "./icons";
import {
  useBridgeFlow,
  type BridgeFlow as BridgeFlowState,
} from "../model/useBridgeFlow";
import type {
  BridgeApprovalInfo,
  BridgeApproveFn,
  BridgeFailedInfo,
  BridgeGetApprovalFn,
  BridgeGetToTokensFn,
  BridgeHistoryItem,
  BridgeInfoRow,
  BridgeInfoTokenRef,
  BridgePathType,
  BridgeQuoteFn,
  BridgeSubmittedInfo,
  BridgeSubmitFn,
  BridgeToken,
  BridgeTxSummary,
} from "../types";
import "../styles/bridge-flow.css";

export interface BridgeFlowProps {
  walletAddress: string;
  tokens: BridgeToken[];
  initialFromToken?: BridgeToken;
  initialToToken?: BridgeToken;
  history?: BridgeHistoryItem[];
  initialSlippage?: string;
  termsUrl?: string;
  env?: Environment;
  getQuote?: BridgeQuoteFn;
  getToTokens?: BridgeGetToTokensFn;
  getApproval?: BridgeGetApprovalFn;
  approveBridge?: BridgeApproveFn;
  submitBridge?: BridgeSubmitFn;
  onSubmitted?: (info: BridgeSubmittedInfo) => void;
  onSuccess?: (info: BridgeSubmittedInfo) => void;
  onFailed?: (info: BridgeFailedInfo) => void;
  onClose?: () => void;
  onBackToWallet?: () => void;
  /**
   * 이 거래의 수수료를 서비스가 대납하는지. 견적(`BridgeTxSummary.txFeeInfo`)의
   * `isDelegateFee` 는 가스만 가리키므로, 스왑/브릿지 수수료 대납은 호출부가
   * 알려줘야 한다(Get ONEUSD 는 `pairs.swap/bridge.feeDelegated` 로 안다).
   */
  feeDelegated?: boolean;
  /** Compact modal mode: fixed direction, no history entry, host-provided title. */
  variant?: "default" | "embedded";
  title?: string;
  /** Embedded mode only. Defaults to whether `walletAddress` is present. */
  isConnected?: boolean;
  /** Opens the host wallet connection flow from the embedded CTA. */
  onRequestConnect?: () => void;
  className?: string;
}

type HistoryFilter = "all" | "completed" | "pending";

const ThemeContext = React.createContext<string | undefined>(undefined);

function useThemeAttr(): string | undefined {
  return React.useContext(ThemeContext);
}

function useDetectTheme(ref: React.RefObject<HTMLElement | null>): string | undefined {
  const [theme, setTheme] = React.useState<string | undefined>(undefined);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => {
      const closest = el.closest("[data-theme]");
      setTheme(closest?.getAttribute("data-theme") ?? undefined);
    };
    read();
    const observer = new MutationObserver(read);
    observer.observe(document.documentElement, { attributes: true, subtree: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, [ref]);
  return theme;
}

const SLIPPAGE_PRESETS = [
  { value: "0.1", label: "0.1%" },
  { value: "0.5", label: "0.5%" },
  { value: "1", label: "1%" },
  { value: "5", label: "5%" },
] as const;

const HISTORY_FILTERS: Array<{ value: HistoryFilter; label: string }> = [
  { value: "all", label: "ALL" },
  { value: "completed", label: "Completed" },
  { value: "pending", label: "Pending" },
];

function formatSlippageLabel(value: string, effective?: string): string {
  // In Auto mode show the resolved percent alongside the label (e.g. "Auto 1%"),
  // matching crossd's chip.
  if (value === "auto") return effective ? `Auto ${effective}%` : "Auto";
  return value ? `${value}%` : "1%";
}

function sanitizeSlippageInput(value: string): string {
  const cleaned = value.replace(/[^\d.]/g, "");
  const [integer = "", ...decimalParts] = cleaned.split(".");
  const decimal = decimalParts.join("");
  if (decimalParts.length === 0) return integer;
  return `${integer}.${decimal.slice(0, 2)}`;
}

function tokenKey(token: BridgeToken): string {
  return `${token.chainId}:${token.address.toLowerCase()}`;
}

function formatBalance(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value || "0";
  return n.toLocaleString("en-US", {
    maximumFractionDigits: n >= 1 ? 2 : 6,
  });
}

function formatUsd(amount: string, token?: BridgeToken): string {
  const n = Number(amount || "0");
  const price = token?.priceUsd ?? 0;
  const value = Number.isFinite(n) ? n * price : 0;
  return `≈ $${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} USD`;
}

// Truncate (floor) a decimal string to at most `maxDp` fraction digits for
// display, trimming trailing zeros. Display-only — never reused in computation.
function clampDecimals(value: string, maxDp = 6): string {
  if (!value) return value;
  const [intPart, fracPart = ""] = value.split(".");
  if (!fracPart) return intPart;
  const truncated = fracPart.slice(0, maxDp).replace(/0+$/, "");
  return truncated ? `${intPart}.${truncated}` : intPart;
}

function exceedsBalance(amount: string, token?: BridgeToken): boolean {
  if (!token) return false;
  const value = parseNumeric(amount);
  const balance = parseNumeric(token.balance);
  if (!value || !balance) return false;
  return value.isGreaterThan(balance);
}



function formatRateDecimal(value: BigNumber): string {
  if (!value.isFinite() || value.isNaN()) return "0";
  const fixed = value.decimalPlaces(6, BigNumber.ROUND_DOWN).toFixed(6);
  const trimmed = fixed.replace(/\.?0+$/, "");
  const [integer = "0", decimal] = trimmed.split(".");
  const integerWithCommas = new BigNumber(integer || "0").toFormat(0);
  return decimal ? `${integerWithCommas}.${decimal}` : integerWithCommas;
}

function shortHash(hash: string): string {
  if (hash.length <= 14) return hash;
  return `${hash.slice(0, 6)}...${hash.slice(-6)}`;
}

function formatHistoryTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return timestamp;

  const pad = (value: number) => value.toString().padStart(2, "0");
  return [
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`,
  ].join(" ");
}

function formatHistoryTokenAmount(value: string): string {
  const amount = parseNumeric(value);
  if (!amount) return value || "0";
  return formatRateDecimal(amount);
}

function formatHistoryUsd(amount: string, token?: BridgeToken): string | null {
  if (token?.priceUsd === undefined) return null;
  const parsed = parseNumeric(amount);
  if (!parsed) return null;
  const value = parsed.multipliedBy(token.priceUsd);
  if (!value.isFinite()) return null;
  return `$${value.toFormat(2)}`;
}

function getHistoryStatus(
  item: BridgeHistoryItem,
): "pending" | "completed" | "failed" {
  if (item.status === "success") return "completed";
  if (item.status === "failed") return "failed";
  return "pending";
}

function historyMatchesFilter(item: BridgeHistoryItem, filter: HistoryFilter): boolean {
  if (filter === "all") return true;
  return getHistoryStatus(item) === filter;
}

function historyStatusLabel(status: ReturnType<typeof getHistoryStatus>): string {
  if (status === "completed") return "Completed";
  if (status === "failed") return "Failed";
  return "Pending";
}

function chainLabel(chainId: number): string {
  if (chainId === 56 || chainId === 97) return "BSC";
  if (chainId === 612044 || chainId === 612055 || chainId === 5555) return "ONEchain";
  return `Chain ${chainId}`;
}

function sameBridgeAsset(a?: BridgeToken, b?: BridgeToken): boolean {
  return (
    !!a &&
    !!b &&
    a.chainId === b.chainId &&
    a.address.toLowerCase() === b.address.toLowerCase()
  );
}

function inferPathType(
  from?: BridgeToken,
  to?: BridgeToken,
): BridgePathType | undefined {
  if (!from || !to) return undefined;
  if (from.chainId === to.chainId) return "swap";
  if (
    sameBridgeAsset(from, to) ||
    from.symbol.toLowerCase() === to.symbol.toLowerCase()
  ) {
    return "bridge";
  }
  return "swap-bridge";
}

function isDirectSwapType(pathType?: BridgePathType): boolean {
  return (
    pathType === "swap" ||
    pathType === "deposit-swap" ||
    pathType === "swap-withdraw"
  );
}

function isZeroAmount(value: string): boolean {
  return !value || value === "0" || Number(value) === 0;
}

function getQuoteFieldErrors(message: string): {
  from?: string;
  to?: string;
} {
  const normalized = message.trim().toLowerCase();
  if (!normalized) return {};

  if (normalized.startsWith("minimum ")) {
    return { from: message };
  }

  if (normalized.startsWith("available ") && normalized.includes("or more")) {
    return { to: message };
  }

  if (
    normalized.includes("insufficient balance") ||
    normalized.includes("not enough balance") ||
    normalized.includes("exceeds balance") ||
    normalized.includes("insufficient network fee")
  ) {
    return { from: message };
  }

  if (
    normalized.includes("insufficient liquidity") ||
    normalized.includes("not enough liquidity")
  ) {
    return { to: message };
  }

  return {};
}

function defaultSummary(flow: BridgeFlowState): BridgeTxSummary {
  const from = flow.fromToken;
  const to = flow.toToken;
  const pathType = inferPathType(from, to);
  const fromAmount = flow.fromAmount || "0";
  const toAmount = flow.toAmount || "0";
  const showBridge = pathType !== undefined && !isDirectSwapType(pathType);
  const showSwap = pathType !== undefined && pathType !== "bridge";

  return {
    pathType,
    bridgeInfo: showBridge ? [
      {
        label: "Route",
        value: from && to ? `${chainLabel(from.chainId)} → ${chainLabel(to.chainId)}` : "-",
      },
      {
        label: "Validator Fee",
        value: isZeroAmount(fromAmount) ? "0" : "Free",
        tone: isZeroAmount(fromAmount) ? "default" : "accent",
      },
      {
        label: "Network Fee",
        value: isZeroAmount(fromAmount) ? "0" : "Free",
        tone: isZeroAmount(fromAmount) ? "default" : "accent",
      },
    ] : undefined,
    swapInfo: showSwap ? [
      from && to
        ? {
            label: "Route",
            value: "",
            routeTokens: [
              { symbol: from.symbol, iconUrl: from.iconUrl },
              { symbol: to.symbol, iconUrl: to.iconUrl },
            ],
          }
        : { label: "Route", value: "-" },
      { label: "Price Impact", value: "0%" },
      {
        label: "In Amount",
        value: fromAmount,
        valueToken: from ? { symbol: from.symbol, iconUrl: from.iconUrl } : undefined,
      },
      {
        label: "Swap Fee",
        value: "0",
        valueToken: from ? { symbol: from.symbol, iconUrl: from.iconUrl } : undefined,
      },
      {
        label: "Out Amount",
        value: toAmount,
        valueToken: to ? { symbol: to.symbol, iconUrl: to.iconUrl } : undefined,
      },
    ] : undefined,
    txFee: [
      { label: "Priority Fee Per Gas", value: "0 Gwei" },
      { label: "Max Fee Per Gas", value: "0 Gwei" },
      { label: "Gas Limit", value: "0" },
    ],
    txFeeInfo: {
      estTxFee: "0",
      isDelegateFee: false,
    },
  };
}

function TokenIcon({ token, size = 20 }: { token?: BridgeToken; size?: number }) {
  const iconUrl = token?.iconUrl;
  const hasImage = Boolean(iconUrl);
  return (
    <span
      className="bf-token-icon"
      data-has-image={hasImage ? "true" : "false"}
      style={{ width: size, height: size }}
    >
      {iconUrl ? (
        <img src={iconUrl} alt="" width={size} height={size} draggable={false} />
      ) : (
        <span className="bf-token-fallback">{token?.symbol?.slice(0, 1) ?? "?"}</span>
      )}
    </span>
  );
}

/**
 * 다음 토큰. 선택지가 사실상 둘뿐이라(브릿지 대상 쌍은 항상 2개) pill 클릭은
 * 목록을 펼치는 게 아니라 곧바로 상대 토큰으로 바꾼다 — 두 항목짜리 드롭다운은
 * 클릭을 한 번 더 요구하는 토글일 뿐이다. 셋 이상이 되어도 순환으로 동작한다.
 */
function nextToken(
  choices: BridgeToken[],
  current?: BridgeToken,
): BridgeToken | undefined {
  if (choices.length === 0) return undefined;
  const index = choices.findIndex((token) => sameBridgeAsset(token, current));
  return choices[(index + 1) % choices.length];
}

function BridgeAmountPanel({
  label,
  token,
  amount,
  usd,
  onAmountChange,
  onTokenClick,
  locked = false,
}: {
  label: "From" | "To";
  token?: BridgeToken;
  amount: string;
  usd: string;
  onAmountChange: (value: string) => void;
  onTokenClick?: () => void;
  // When the side has no real choice (e.g. a single available "to" token), the
  // pill is fixed: hide the chevron and make it non-interactive.
  locked?: boolean;
}) {
  return (
    <section className="bf-amount-panel">
      <div className="bf-amount-head">
        <span>{label}</span>
        <span className="bf-balance">
          {formatBalance(token?.balance ?? "0")} {token?.symbol ?? ""}
        </span>
      </div>
      <div className="bf-amount-main">
        <label className="bf-amount-input-wrap">
          <span className="bf-sr-only">{label} amount</span>
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(event) => onAmountChange(event.target.value)}
            placeholder="0"
            className="bf-amount-input"
            data-size={amountInputSize(amount)}
          />
          <span className="bf-amount-usd">{usd}</span>
        </label>
        <button
          type="button"
          className="bf-token-pill"
          onClick={locked ? undefined : onTokenClick}
          disabled={!locked && !onTokenClick}
          data-locked={locked ? "true" : undefined}
          data-track={locked ? undefined : "token_switch"}
          data-track-id={label.toLowerCase()}
          aria-label={`Switch ${label.toLowerCase()} token`}
        >
          <span className="bf-token-pill-face" key={token ? tokenKey(token) : "none"}>
            <TokenIcon token={token} />
            <span>{token?.symbol ?? "Select"}</span>
          </span>
          {!locked && <ChevronDownIcon size={16} />}
        </button>
      </div>
    </section>
  );
}

function formatEmbeddedAmount(value: string): string {
  if (!value) return "";
  const [integer = "", decimal] = value.replace(/,/g, "").split(".");
  const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decimal === undefined ? formattedInteger : `${formattedInteger}.${decimal}`;
}

function amountInputSize(value: string): "large" | "medium" | "small" {
  const length = value.replace(/,/g, "").trim().length;
  if (length > 26) return "small";
  if (length > 18) return "medium";
  return "large";
}

function WalletGlyph() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M2.333 4.667A1.667 1.667 0 0 1 4 3h7.333A1.667 1.667 0 0 1 13 4.667v6.666A1.667 1.667 0 0 1 11.333 13H4a1.667 1.667 0 0 1-1.667-1.667V4.667Z"
        stroke="currentColor"
        strokeWidth="1.25"
      />
      <path
        d="M2.667 5.333h9.666c.737 0 1.334.597 1.334 1.334v2.666c0 .737-.597 1.334-1.334 1.334H9.667A2.667 2.667 0 0 1 7 8a2.667 2.667 0 0 1 2.667-2.667Z"
        stroke="currentColor"
        strokeWidth="1.25"
      />
      <circle cx="10" cy="8" r=".75" fill="currentColor" />
    </svg>
  );
}

function EmbeddedAmountPanel({
  label,
  token,
  amount,
  onAmountChange,
  onTokenClick,
  onMax,
  locked,
}: {
  label: "From" | "To";
  token?: BridgeToken;
  amount: string;
  onAmountChange: (value: string) => void;
  onTokenClick?: () => void;
  onMax?: () => void;
  locked: boolean;
}) {
  const isFrom = label === "From";
  return (
    <section className="bf-embedded-amount-panel">
      <div className="bf-embedded-amount-head">
        <span className="bf-embedded-amount-label">
          <strong>{label}</strong>
          {!isFrom && <em>estimated</em>}
        </span>
        {token && (
          <span className="bf-embedded-balance">
            <WalletGlyph />
            <span className="bf-embedded-balance-value">
              {formatBalance(token.balance)}
            </span>
            <span>{token.symbol}</span>
            {onMax && (
              <button
                type="button"
                onClick={onMax}
                aria-label="Use maximum available balance"
              >
                MAX
              </button>
            )}
          </span>
        )}
      </div>
      <div className="bf-embedded-amount-main">
        <button
          type="button"
          className="bf-embedded-token-pill"
          onClick={locked ? undefined : onTokenClick}
          disabled={locked}
          data-locked={locked ? "true" : undefined}
          aria-label={`Switch ${label.toLowerCase()} token`}
        >
          <span className="bf-token-pill-face" key={token ? tokenKey(token) : "none"}>
            <TokenIcon token={token} />
            <span>{token?.symbol ?? "Select"}</span>
          </span>
          {!locked && <ChevronDownIcon size={14} />}
        </button>
        <label className="bf-embedded-input-wrap">
          <span className="bf-sr-only">{label} amount</span>
          <input
            type="text"
            inputMode="decimal"
            value={formatEmbeddedAmount(amount)}
            onChange={(event) => onAmountChange(event.target.value)}
            placeholder="0.00"
            className="bf-embedded-amount-input"
            data-size={amountInputSize(amount)}
          />
        </label>
      </div>
    </section>
  );
}

function BridgeSlippageSettings({
  flow,
  onClose,
}: {
  flow: BridgeFlowState;
  onClose: () => void;
}) {
  const titleId = React.useId();
  const [portalTarget, setPortalTarget] = React.useState<HTMLElement | null>(null);
  const selectedPreset = SLIPPAGE_PRESETS.find(
    (preset) => preset.value === flow.slippage,
  );
  const [customDraft, setCustomDraft] = React.useState(
    flow.slippage && !selectedPreset ? flow.slippage : "",
  );
  const hasCustomDraft = customDraft.trim() !== "" && customDraft.trim() !== ".";

  const customValue = hasCustomDraft ? Number(customDraft) : NaN;
  const exceedsMax = Number.isFinite(customValue) && customValue > 50;
  const highSlippage =
    Number.isFinite(customValue) && customValue > 5 && !exceedsMax;

  const selectPreset = (value: string) => {
    flow.setSlippage(value);
    onClose();
  };

  const applyCustom = () => {
    if (!hasCustomDraft || exceedsMax) return;
    flow.setSlippage(sanitizeSlippageInput(customDraft));
    onClose();
  };

  React.useEffect(() => {
    setPortalTarget(document.body);
  }, []);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  if (!portalTarget) return null;

  const theme = useThemeAttr();

  return createPortal(
    <div
      className="bf-slippage-overlay"
      data-theme={theme}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="bf-slippage-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="bf-slippage-head">
          <h3 id={titleId}>Slippage Settings</h3>
          <button
            type="button"
            className="bf-slippage-close"
            onClick={onClose}
            aria-label="Close slippage settings"
          >
            <CloseIcon size={24} />
          </button>
        </header>

        <div className="bf-slippage-body">
          <p className="bf-slippage-desc">
            The maximum price difference you are willing to accept before the
            transaction is reverted. Higher slippage increases the chance of
            execution but may result in a worse price.
          </p>

          <div className="bf-slippage-controls">
            <div className="bf-slippage-presets">
              {SLIPPAGE_PRESETS.map((preset) => (
                <button
                  key={preset.value}
                  type="button"
                  className="bf-slippage-preset"
                  data-selected={
                    !hasCustomDraft && flow.slippage === preset.value
                      ? "true"
                      : "false"
                  }
                  onClick={() => selectPreset(preset.value)}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            <label
              className="bf-slippage-custom"
              data-selected={hasCustomDraft ? "true" : "false"}
            >
              <span className="bf-sr-only">Custom slippage percent</span>
              <input
                value={customDraft}
                onChange={(event) => {
                  setCustomDraft(sanitizeSlippageInput(event.target.value));
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    applyCustom();
                  }
                }}
                inputMode="decimal"
                placeholder="Enter custom slippage"
                autoComplete="off"
                spellCheck={false}
              />
              <span aria-hidden="true">%</span>
            </label>
          </div>

          {exceedsMax ? (
            <p className="bf-slippage-message" data-variant="error">
              Slippage cannot exceed 50%.
            </p>
          ) : highSlippage ? (
            <p className="bf-slippage-message" data-variant="warning">
              Your transaction may be frontrun due to high slippage.
            </p>
          ) : null}

          {hasCustomDraft && (
            <button
              type="button"
              className="bf-slippage-apply"
              onClick={applyCustom}
              disabled={exceedsMax}
            >
              Apply
            </button>
          )}
        </div>
      </section>
    </div>,
    portalTarget,
  );
}

function BridgeTermsModal({
  flow,
  env,
}: {
  flow: BridgeFlowState;
  env: Environment;
}) {
  const titleId = React.useId();
  const [checked, setChecked] = React.useState(false);
  const [portalTarget, setPortalTarget] = React.useState<HTMLElement | null>(null);
  const theme = useThemeAttr();
  // 호출부 prop > apps.json(legal.termsUrl) > dapp-ui 공통 폴백 순.
  const remoteTermsUrl = useRemoteConfigValue<AppsConfig, string>(
    "apps.json",
    env,
    (config) => resolveEnvValue(config?.legal?.termsUrl, env),
  );
  const termsUrl = flow.termsUrl ?? remoteTermsUrl ?? DEFAULT_ONEUSD_TERMS_URL;

  React.useEffect(() => {
    setPortalTarget(document.body);
  }, []);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") flow.cancelTerms();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [flow]);

  const [visible, animState] = useExitDelay(flow.termsOpen);

  if (!portalTarget || !visible) return null;

  return createPortal(
    <div
      className="bf-action-modal-overlay"
      data-theme={theme}
      data-state={animState}
      role="presentation"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) flow.cancelTerms();
      }}
    >
      <section
        className="bf-action-modal bf-terms-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <h3 id={titleId}>Agree to Terms of Service</h3>
        <p className="bf-action-modal-desc">
          Please make sure to review the Terms of Service before using ONEUSD.
        </p>

        <div className="bf-terms-check-row" data-checked={checked ? "true" : "false"}>
          <button
            type="button"
            className="bf-terms-check-button"
            role="checkbox"
            aria-checked={checked}
            aria-label="Agree to Terms of Use"
            data-track="terms_check"
            onClick={() => setChecked((value) => !value)}
          >
            <span
              className="bf-terms-checkbox"
              data-checked={checked ? "true" : "false"}
              aria-hidden="true"
            />
          </button>
          <div className="bf-terms-copy">
            {/* Bigger hit target for the checkbox next to it. Keyboard/a11y stays
                on the role="checkbox" button, so this stays a plain span. */}
            <span
              className="bf-terms-label"
              onClick={() => setChecked((value) => !value)}
            >
              Agree to Terms of Use
            </span>
            {termsUrl && (
              <a
                href={termsUrl}
                target="_blank"
                rel="noreferrer"
              >
                Terms of Service
              </a>
            )}
          </div>
        </div>

        <p className="bf-terms-privacy">
          I have read and acknowledged the Privacy Policy to use ONEUSD
        </p>

        <button
          type="button"
          className="bf-action-primary"
          disabled={!checked || flow.status === "submitting"}
          onClick={flow.confirmTerms}
          data-track="terms_confirm"
        >
          Confirm
        </button>
      </section>
    </div>,
    portalTarget,
  );
}

function CopyButton({ value, disabled = false }: { value: string; disabled?: boolean }) {
  const [copied, setCopied] = React.useState(false);
  return (
    <button
      type="button"
      className="bf-copy-button"
      aria-label="Copy token address"
      disabled={disabled}
      onClick={() => {
        if (disabled) return;
        void navigator.clipboard?.writeText(value).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1200);
        });
      }}
    >
      {copied ? "OK" : "Copy"}
    </button>
  );
}

function BridgeApproveModal({
  flow,
  approval: openApproval,
}: {
  flow: BridgeFlowState;
  /** Null closes the modal -- it stays mounted briefly to run the exit animation. */
  approval: BridgeApprovalInfo | null;
}) {
  const titleId = React.useId();
  const [portalTarget, setPortalTarget] = React.useState<HTMLElement | null>(null);
  const theme = useThemeAttr();

  React.useEffect(() => {
    setPortalTarget(document.body);
  }, []);

  React.useEffect(() => {
    // Stays mounted while closed (for the exit animation), so Escape must only
    // bind while there is an approval on screen.
    if (!openApproval) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (flow.status === "submitting") return;
      if (event.key === "Escape") flow.cancelApproval();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [flow, openApproval]);

  const [approval, animState] = useExitDelay(openApproval);

  if (!portalTarget || !approval) return null;

  const disabled = flow.status === "submitting" || !flow.approveBridge;
  const isApproving = flow.status === "submitting";

  return createPortal(
    <div
      className="bf-action-modal-overlay"
      data-theme={theme}
      data-state={animState}
      role="presentation"
    >
      <section
        className="bf-action-modal bf-approve-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <h3 id={titleId}>Approve {approval.token.symbol}</h3>
        <TokenIcon token={approval.token} size={72} />

        <div className="bf-approve-address-box">
          <span>Token Address</span>
          <div>
            <strong>{approval.tokenAddress}</strong>
            <CopyButton value={approval.tokenAddress} disabled={isApproving} />
          </div>
        </div>

        <div className="bf-action-buttons">
          <button
            type="button"
            className="bf-action-secondary"
            onClick={flow.cancelApproval}
            disabled={flow.status === "submitting"}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bf-action-primary"
            onClick={flow.confirmApproval}
            disabled={disabled}
            aria-busy={isApproving}
            data-track="approve_confirm"
          >
            {isApproving && <span className="bf-action-button-spinner" aria-hidden="true" />}
            <span>{isApproving ? "Approving..." : "Approve"}</span>
          </button>
        </div>
      </section>
    </div>,
    portalTarget,
  );
}

function BridgeApprovalSuccessModal({ flow }: { flow: BridgeFlowState }) {
  const titleId = React.useId();
  const [portalTarget, setPortalTarget] = React.useState<HTMLElement | null>(null);
  const theme = useThemeAttr();

  React.useEffect(() => {
    setPortalTarget(document.body);
  }, []);

  React.useEffect(() => {
    if (!flow.approvalSuccessOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") flow.cancelApproval();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [flow]);

  const [visible, animState] = useExitDelay(flow.approvalSuccessOpen);

  if (!portalTarget || !visible) return null;
  const isContinuing = flow.status === "submitting";

  return createPortal(
    <div
      className="bf-action-modal-overlay"
      data-theme={theme}
      data-state={animState}
      role="presentation"
    >
      <section
        className="bf-action-modal bf-approval-success-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <h3 id={titleId}>Approval Success</h3>
        <span className="bf-success-mark" aria-hidden="true" />
        <p className="bf-action-modal-desc">
          Token approval confirmed successfully.
          <br />
          Please continue to the next step.
        </p>

        <div className="bf-action-buttons">
          <button
            type="button"
            className="bf-action-secondary"
            onClick={flow.cancelApproval}
            disabled={isContinuing}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bf-action-primary"
            onClick={flow.continueAfterApproval}
            disabled={isContinuing}
            aria-busy={isContinuing}
          >
            {isContinuing && <span className="bf-action-button-spinner" aria-hidden="true" />}
            <span>{isContinuing ? "Continuing..." : "Continue"}</span>
          </button>
        </div>
      </section>
    </div>,
    portalTarget,
  );
}

function ExchangeRate({
  fromAmount,
  toAmount,
  fromToken,
  toToken,
}: {
  fromAmount: string;
  toAmount: string;
  fromToken?: BridgeToken;
  toToken?: BridgeToken;
}) {
  const exchangeRate = React.useMemo(() => {
    const from = parseNumeric(fromAmount);
    const to = parseNumeric(toAmount);
    if (!from || !to || from.lte(0) || to.lte(0)) return null;
    return to.dividedBy(from);
  }, [fromAmount, toAmount]);

  // Same token across different chains (e.g. CROSS on BNB <-> CROSS on Cross)
  // always bridges 1:1, so the "1 CROSS = 1 CROSS" rate adds no information.
  const visible = Boolean(
    exchangeRate && fromToken && toToken && fromToken.symbol !== toToken.symbol,
  );

  return (
    <Collapse open={visible}>
      <div className="bf-exchange-rate">
        <span>1 {fromToken?.symbol}</span>
        <span aria-hidden="true">=</span>
        <span>
          {exchangeRate ? formatRateDecimal(exchangeRate) : ""} {toToken?.symbol}
        </span>
      </div>
    </Collapse>
  );
}

function BridgeStatusToast({ flow }: { flow: BridgeFlowState }) {
  if (!flow.submitToast) return null;
  return (
    <div
      className="bf-status-toast"
      data-variant="processing"
      role="status"
      aria-live="polite"
    >
      <span className="bf-status-toast-icon" aria-hidden="true">
        <span className="bf-status-spinner" />
      </span>
      <span className="bf-status-toast-copy">
        <strong>Transaction Processing</strong>
        <span>Your transaction is being processed on the blockchain</span>
      </span>
    </div>
  );
}

function BridgeErrorToast({ message }: { message: string }) {
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    if (!message) {
      setVisible(false);
      return undefined;
    }

    setVisible(true);
    const timer = window.setTimeout(() => {
      setVisible(false);
    }, 3500);

    return () => window.clearTimeout(timer);
  }, [message]);

  if (!message || !visible) return null;

  return (
    <div
      className="bf-status-toast"
      data-variant="error"
      role="alert"
      aria-live="assertive"
    >
      <span className="bf-status-toast-icon" aria-hidden="true">
        <span className="bf-status-error-mark">!</span>
      </span>
      <span className="bf-status-toast-copy">
        <strong>Error</strong>
        <span>{message}</span>
      </span>
    </div>
  );
}

function successTitle(pathType?: BridgePathType): string {
  switch (pathType) {
    case "swap":
    case "deposit-swap":
    case "swap-withdraw":
      return "Swap Transaction Sent";
    case "bridge":
      return "Bridge Transaction Sent";
    case "swap-bridge":
      return "Swap & Bridge Transaction Sent";
    case "bridge-swap":
      return "Bridge & Swap Transaction Sent";
    default:
      return "Transaction Sent";
  }
}

function BridgeSuccessModal({ flow }: { flow: BridgeFlowState }) {
  const titleId = React.useId();
  const [portalTarget, setPortalTarget] = React.useState<HTMLElement | null>(null);
  const submitted = flow.submitted;
  const theme = useThemeAttr();

  React.useEffect(() => {
    setPortalTarget(document.body);
  }, []);

  React.useEffect(() => {
    if (!flow.successDialogOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") flow.dismissSuccessDialog();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [flow]);

  if (!portalTarget || !flow.successDialogOpen || !submitted) return null;

  return createPortal(
    <div className="bf-success-overlay" data-theme={theme} role="presentation">
      <section
        className="bf-success-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="bf-success-body">
          <span className="bf-success-mark" aria-hidden="true" />
          <div className="bf-success-copy">
            <strong id={titleId}>{successTitle(submitted.pathType)}</strong>
            <span>Please check the results in the history.</span>
          </div>

          <div className="bf-success-route" aria-hidden="true">
            {(submitted.routeTokens && submitted.routeTokens.length > 0
              ? submitted.routeTokens
              : [
                  { symbol: submitted.fromToken.symbol, iconUrl: submitted.fromToken.iconUrl },
                  { symbol: submitted.toToken.symbol, iconUrl: submitted.toToken.iconUrl },
                ]
            ).map((token, index) => (
              <React.Fragment key={`${token.symbol}-${index}`}>
                {index > 0 && <span className="bf-success-route-arrow">→</span>}
                <InfoTokenGlyph token={token} size={43} />
              </React.Fragment>
            ))}
          </div>

          <button
            type="button"
            className="bf-success-ok"
            onClick={flow.dismissSuccessDialog}
            data-track="success_ok"
          >
            OK
          </button>
        </div>
      </section>
    </div>,
    portalTarget,
  );
}

function InfoTokenGlyph({
  token,
  size = 16,
}: {
  token: BridgeInfoTokenRef;
  size?: number;
}) {
  const hasImage = Boolean(token.iconUrl);
  return (
    <span
      className="bf-token-icon"
      data-has-image={hasImage ? "true" : "false"}
      style={{ width: size, height: size }}
    >
      {token.iconUrl ? (
        <img src={token.iconUrl} alt="" width={size} height={size} draggable={false} />
      ) : (
        <span className="bf-token-fallback">{token.symbol?.slice(0, 1) ?? "?"}</span>
      )}
    </span>
  );
}

function InfoRowValue({ row }: { row: BridgeInfoRow }) {
  if (row.routeTokens && row.routeTokens.length > 0) {
    return (
      <span className="bf-info-route">
        {row.routeTokens.map((token, index) => (
          <React.Fragment key={`${token.symbol}-${index}`}>
            {index > 0 && (
              <span className="bf-info-route-arrow" aria-hidden="true">
                →
              </span>
            )}
            <InfoTokenGlyph token={token} />
          </React.Fragment>
        ))}
      </span>
    );
  }
  if (row.valueToken) {
    return (
      <span className="bf-info-value-token">
        <InfoTokenGlyph token={row.valueToken} />
        <span>{row.value}</span>
      </span>
    );
  }
  return <>{row.value}</>;
}

function InfoRows({ rows }: { rows?: BridgeInfoRow[] }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="bf-info-rows">
      {rows.map((row) => (
        <div className="bf-info-row" key={`${row.label}-${row.value}`}>
          <span className="bf-info-label">{row.label}</span>
          <span className="bf-info-value" data-tone={row.tone ?? "default"}>
            <InfoRowValue row={row} />
          </span>
        </div>
      ))}
    </div>
  );
}

function getInfoTitle(
  summary: BridgeTxSummary,
  hasBridgeInfo: boolean,
  hasSwapInfo: boolean,
): string {
  if (summary.pathType === "bridge") return "Bridge Info";
  if (isDirectSwapType(summary.pathType)) return "Swap Info";
  if (hasBridgeInfo && hasSwapInfo) return "Swap & Bridge Info";
  if (hasBridgeInfo) return "Bridge Info";
  return "Swap Info";
}

/**
 * 금액 입력에 따라 붙었다 사라지는 하단 info 블록의 높이 전환.
 *
 * 닫힐 때도 내용이 남아 있어야 접히는 게 보이므로, `open` 이 꺼지면 마지막으로
 * 열려 있던 children 을 그대로 붙잡고 접는다. 높이는 grid `0fr → 1fr` —
 * `height: auto` 를 트랜지션할 수 있는 가장 무난한 방법이고, 내용 높이를 미리
 * 재거나 JS 타이머를 둘 필요가 없다.
 */
function Collapse({
  open,
  className,
  children,
}: {
  open: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  const lastOpenChildren = React.useRef<React.ReactNode>(null);
  if (open) lastOpenChildren.current = children;
  return (
    <div
      className={className ? `bf-collapse ${className}` : "bf-collapse"}
      data-open={open ? "true" : "false"}
      aria-hidden={open ? undefined : true}
    >
      <div className="bf-collapse-inner">
        {open ? children : lastOpenChildren.current}
      </div>
    </div>
  );
}

function BridgeDetails({
  summary,
  from,
  to,
  feeDelegated,
}: {
  summary: BridgeTxSummary;
  from?: RouteEndpoint;
  to?: RouteEndpoint;
  feeDelegated?: boolean;
}) {
  const hasBridgeInfo = Boolean(summary.bridgeInfo?.length);
  const hasSwapInfo = Boolean(summary.swapInfo?.length);
  const hasTxFee = Boolean(summary.txFee?.length);
  const infoTitle = getInfoTitle(summary, hasBridgeInfo, hasSwapInfo);
  const [infoOpen, setInfoOpen] = React.useState(true);
  const [txFeeOpen, setTxFeeOpen] = React.useState(true);

  return (
    <div className="bf-details">
      {summary.liquidity && (
        <section className="bf-liquidity">
          <div className="bf-liquidity-head">
            <span>{summary.liquidity.label ?? "Available Liquidity"}</span>
            <span className="bf-liquidity-status" data-status={summary.liquidity.status}>
              {summary.liquidity.status ?? "Normal"}
            </span>
          </div>
          <div className="bf-liquidity-track">
            <span style={{ width: `${summary.liquidity.percentage ?? 50}%` }} />
          </div>
        </section>
      )}
      {(hasBridgeInfo || hasSwapInfo) && (
        <section
          className="bf-detail-section bf-info-card"
          data-open={infoOpen ? "true" : "false"}
        >
          <button
            type="button"
            className="bf-collapse-head"
            aria-expanded={infoOpen}
            onClick={() => setInfoOpen((open) => !open)}
            data-track="details_toggle"
          >
            <h3>{infoTitle}</h3>
            <ChevronDownIcon size={16} className="bf-collapse-chevron" />
          </button>
          {infoOpen && (
            <div className="bf-collapse-body">
              <RouteStepSections
                summary={summary}
                from={from}
                to={to}
                feeDelegated={feeDelegated}
              />
            </div>
          )}
        </section>
      )}
      {hasTxFee && (
        <section
          className="bf-detail-section bf-tx-fee-section"
          data-open={txFeeOpen ? "true" : "false"}
        >
          <button
            type="button"
            className="bf-collapse-head bf-tx-fee-head"
            aria-expanded={txFeeOpen}
            onClick={() => setTxFeeOpen((open) => !open)}
            data-track="details_toggle"
          >
            <h3>Est. Tx Fee</h3>
            <div className="bf-tx-fee-total">
              {summary.txFeeInfo?.isDelegateFee && (
                <span className="bf-fee-delegated">Fee Delegated</span>
              )}
              {summary.txFeeInfo?.tokenIconUrl && (
                <img
                  className="bf-tx-fee-token"
                  src={summary.txFeeInfo.tokenIconUrl}
                  alt={summary.txFeeInfo.tokenSymbol ?? ""}
                  width={16}
                  height={16}
                  draggable={false}
                />
              )}
              <span
                data-delegated={
                  summary.txFeeInfo?.isDelegateFee &&
                  Number(summary.txFeeInfo?.estTxFee) > 0
                    ? "true"
                    : undefined
                }
              >
                {clampDecimals(summary.txFeeInfo?.estTxFee ?? "0")}
              </span>
              <ChevronDownIcon size={16} className="bf-collapse-chevron" />
            </div>
          </button>
          {txFeeOpen && (
            <div className="bf-collapse-body">
              <InfoRows rows={summary.txFee} />
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function normalizeEmbeddedSummary(summary: BridgeTxSummary): BridgeTxSummary {
  return {
    ...summary,
    bridgeInfo: summary.bridgeInfo?.map((row) => ({
      ...row,
      value:
        row.label === "Route"
          ? row.value.replace(/\bCROSS\b/g, "ONEchain")
          : row.value,
    })),
    swapInfo: summary.swapInfo?.map((row) => ({
      ...row,
      label: row.label.startsWith("Swap Fee") ? "Est. Swap Fee" : row.label,
    })),
  };
}

/** 입력 금액을 스텝 표기용으로 정리한다(콤마 + 6dp, 값이 없으면 undefined). */
function formatAmountForStep(value?: unknown): string | undefined {
  const amount = parseNumeric(value);
  if (!amount || amount.lte(0)) return undefined;
  return formatRateDecimal(amount);
}

/**
 * 환율 표시 문자열. 소수 앞자리 0이 많아 읽기 어려운 방향은 뒤집는다 —
 * `1 tONE = 0.000733 tONEUSD` 보다 `1 tONEUSD = 1,362.73 tONE` 이 읽힌다.
 * 값 자체는 같고 표시 방향만 바뀐다.
 */
function formatRateLine(
  fromAmount: string,
  toAmount: string,
  fromToken?: BridgeToken,
  toToken?: BridgeToken,
): string | undefined {
  if (!fromToken || !toToken) return undefined;
  const from = parseNumeric(fromAmount);
  const to = parseNumeric(toAmount);
  if (!from || !to || from.lte(0) || to.lte(0)) return undefined;

  const forward = to.dividedBy(from);
  if (forward.gte("0.001")) {
    return `1 ${fromToken.symbol} = ${formatRateDecimal(forward)} ${toToken.symbol}`;
  }
  const reversed = from.dividedBy(to);
  return `1 ${toToken.symbol} = ${formatRateDecimal(reversed)} ${fromToken.symbol}`;
}

/**
 * Info 영역을 평면 5줄 목록이 아니라 **거래 단계**로 그린다(시안 C). 각 단계는
 * "무엇이 얼마" 한 줄이고, 그 단계에서 발생하는 값(수수료·가격 영향·환율)은
 * 그 아래 메타로 붙어 맥락을 갖는다. swap+bridge 복합 경로에서는 구간별로
 * 단계가 이어진다.
 */
type InfoTone = NonNullable<BridgeInfoRow["tone"]>;

interface RouteStepMeta {
  text: string;
  tone?: InfoTone;
}

interface RouteStep {
  key: string;
  title: string;
  amount: string;
  amountToken?: BridgeInfoTokenRef;
  /** 구간 단계의 경로 표기 — 아이콘 배열로 오는 Route 행을 그대로 그린다. */
  routeTokens?: BridgeInfoTokenRef[];
  meta: RouteStepMeta[];
  tags: string[];
}

/**
 * Price impact 위험도 구간. DEX 관행(경고 1%, 위험 5%)을 따른다 — 1% 미만은
 * 정상 거래라 색을 쓰지 않고, 5% 이상은 사용자가 손실을 인지해야 하는 값이다.
 */
const PRICE_IMPACT_WARNING = 1;
const PRICE_IMPACT_DANGER = 5;

/** "12.5%" / "0.000012" 같은 표기에서 퍼센트 숫자만 뽑는다. 숫자가 아니면 null. */
function parsePercent(value?: unknown): number | null {
  return parseNumeric(value)?.toNumber() ?? null;
}

function priceImpactTone(value?: string): InfoTone | undefined {
  const percent = parsePercent(value);
  if (percent === null) return undefined;
  if (percent >= PRICE_IMPACT_DANGER) return "danger";
  if (percent >= PRICE_IMPACT_WARNING) return "warning";
  return undefined;
}

const IN_AMOUNT = /\bin\b.*amount|amount.*\bin\b|^from\b/i;
const OUT_AMOUNT = /\bout\b.*amount|amount.*\bout\b|^to\b|receive/i;
const FEE_ROW = /fee/i;
const CONTEXT_ROW = /impact|rate|slippage|minimum|min\.|received/i;
const IMPACT_ROW = /impact/i;
const ROUTE_ROW = /^route$/i;

interface RouteSection {
  key: string;
  label: string;
  routeText?: string;
  routeTokens?: BridgeInfoTokenRef[];
  inRow?: BridgeInfoRow;
  outRow?: BridgeInfoRow;
  meta: RouteStepMeta[];
  rest: BridgeInfoRow[];
}

/**
 * 한 구간(swap 또는 bridge)의 행들을 분류한다. 구간마다 담고 있는 것이 다르다 —
 * swapInfo 는 In/Out Amount + Price Impact + Swap Fee 를, bridgeInfo 는 Route +
 * Validator/Network Fee 만 준다(금액 행이 없다). 그래서 금액은 있으면 쓰고,
 * 없으면 상위(from/to 입력값)에서 채운다.
 */
function toRouteSection(
  rows: BridgeInfoRow[],
  key: string,
  label: string,
): RouteSection {
  const inRow = rows.find((row) => IN_AMOUNT.test(row.label));
  const outRow = rows.find((row) => OUT_AMOUNT.test(row.label));
  const used = new Set<BridgeInfoRow>();
  if (inRow) used.add(inRow);
  if (outRow) used.add(outRow);

  // Route 행은 어느 체인을 거치는지를 담고 있어 버리지 않는다. 아이콘 배열
  // (routeTokens) 형태는 문자열로 못 합치므로 아래 목록에 그대로 남긴다.
  const routeRow = rows.find(
    (row) => ROUTE_ROW.test(row.label) || (row.routeTokens?.length ?? 0) > 0,
  );
  const routeTokens = routeRow?.routeTokens?.length
    ? routeRow.routeTokens
    : undefined;
  const routeText = routeRow && !routeTokens ? routeRow.value : undefined;
  if (routeRow) used.add(routeRow);

  const meta: RouteStepMeta[] = [];
  for (const row of rows) {
    if (used.has(row)) continue;
    const isFee = FEE_ROW.test(row.label);
    if (!isFee && !CONTEXT_ROW.test(row.label)) continue;
    used.add(row);
    // 호출부가 값을 "0" 으로 주기도 하고 "Free" 로 주기도 한다.
    const numeric = parsePercent(row.value);
    const isFree =
      isFee && (numeric === 0 || /^\s*free\s*$/i.test(String(row.value ?? "")));
    // swap 구간의 무료 수수료는 아예 적지 않는다 — "Est. Swap Fee Free" 는
    // 읽는 사람에게 아무 정보도 주지 않는다. bridge 구간은 무료가 곧 혜택이라
    // (기존 브릿지 UI 도 오렌지 Free 로 강조했다) 그대로 남긴다.
    if (isFree && key === "swap") continue;
    meta.push({
      text: isFree ? `${row.label} Free` : `${row.label} ${row.value}`,
      tone: isFree
        ? "accent"
        : // Price impact는 값에 따라 색으로 위험도를 표시한다.
          IMPACT_ROW.test(row.label)
          ? row.tone === "danger" || row.tone === "warning"
            ? row.tone
            : priceImpactTone(row.value)
          : undefined,
    });
  }

  return {
    key,
    label,
    routeText,
    routeTokens,
    inRow,
    outRow,
    meta,
    rest: rows.filter((row) => !used.has(row)),
  };
}

function RouteStepList({ steps }: { steps: RouteStep[] }) {
  return (
    <div className="bf-route-steps">
      {steps.map((step, index) => (
        <div
          className="bf-route-step"
          key={step.key}
          data-last={index === steps.length - 1 ? "true" : "false"}
          data-kind={step.key.startsWith("hop-") ? "hop" : "endpoint"}
        >
          <span className="bf-route-rail" aria-hidden="true">
            <span className="bf-route-line" />
            <span
              className="bf-route-dot"
              data-final={index === steps.length - 1 ? "true" : "false"}
            />
          </span>
          <span className="bf-route-body">
            <span className="bf-route-head">
              <span className="bf-route-title">{step.title}</span>
              <span className="bf-route-amount">
                {step.routeTokens?.length ? (
                  step.routeTokens.map((token, tokenIndex) => (
                    <React.Fragment key={`${token.symbol}-${tokenIndex}`}>
                      {tokenIndex > 0 && (
                        <span className="bf-info-route-arrow" aria-hidden="true">
                          →
                        </span>
                      )}
                      <InfoTokenGlyph token={token} />
                    </React.Fragment>
                  ))
                ) : (
                  <>
                    {step.amountToken && (
                      <InfoTokenGlyph token={step.amountToken} />
                    )}
                    {/* key: 값이 바뀌면 새로 마운트돼서 `bf-value-in` 이 다시
                        재생된다 — 아래 info 행들이 쓰는 것과 같은 방식. */}
                    <span className="bf-route-amount-value" key={step.amount}>
                      {step.amount}
                    </span>
                  </>
                )}
              </span>
            </span>
            {(step.meta.length > 0 || step.tags.length > 0) && (
              <span className="bf-route-meta">
                {step.meta.map((item) => (
                  <span key={item.text} data-tone={item.tone ?? "default"}>
                    {item.text}
                  </span>
                ))}
                {step.tags.map((tag) => (
                  <span className="bf-route-tag" key={tag}>
                    {tag}
                  </span>
                ))}
              </span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

/** Price impact 행에 위험도 tone을 채운다(호출부가 이미 줬으면 그대로 둔다). */
function withImpactTone(row: BridgeInfoRow): BridgeInfoRow {
  if (!IMPACT_ROW.test(row.label)) return row;
  if (row.tone === "danger" || row.tone === "warning") return row;
  const tone = priceImpactTone(row.value);
  return tone ? { ...row, tone } : row;
}


export interface RouteEndpoint {
  symbol?: string;
  iconUrl?: string;
  amount?: string;
  /** 이 금액이 나가는/들어오는 체인 이름. 첫 단계가 어느 체인에서 시작하는지는
   *  라우트 행이 아니라 여기서만 알 수 있다(브릿지는 체인을 넘는 거래다). */
  chain?: string;
}

/**
 * 라우트 스텝 + 남은 행. Swap/Bridge 모달(기본)과 Get ONEUSD(embedded)가 같은
 * 표현을 쓰도록 여기 한 곳에서 만든다.
 *
 * 스텝 구성: `Send` → (구간마다 한 단계) → `Receive`. 구간이 하나뿐인 단순
 * 거래에서는 중간 단계를 넣지 않고 그 구간의 값들을 Send/Receive 메타로 붙인다 —
 * 한 줄짜리 "Swap" 단계를 끼워 넣어야 할 이유가 없다. 브릿지가 섞인 복합 경로는
 * 구간별 단계가 있어야 어디서 무엇이 일어나는지 읽힌다.
 */
function RouteStepSections({
  summary,
  rateLine,
  from,
  to,
  feeDelegated: feeDelegatedProp,
  includeTxFee = false,
}: {
  summary: BridgeTxSummary;
  rateLine?: string;
  from?: RouteEndpoint;
  to?: RouteEndpoint;
  /** 스왑/브릿지 수수료 대납 여부(호출부가 안다). 가스 대납은 견적에서 읽는다. */
  feeDelegated?: boolean;
  /** 가스비를 첫 단계(= 실제로 가스가 나가는 체인)에 붙인다. 기본 뷰는 별도
   *  "Est. Tx Fee" 섹션이 있으므로 끄고, embedded 는 그 섹션이 없어 켠다. */
  includeTxFee?: boolean;
}) {
  const feeDelegated =
    Boolean(summary.txFeeInfo?.isDelegateFee) || Boolean(feeDelegatedProp);
  const sections = React.useMemo(() => {
    const order: Array<["bridge" | "swap", string]> =
      summary.pathType === "bridge-swap"
        ? [
            ["bridge", "Bridge"],
            ["swap", "Swap"],
          ]
        : [
            ["swap", "Swap"],
            ["bridge", "Bridge"],
          ];

    return order
      .map(([key, label]) => {
        const rows = key === "swap" ? summary.swapInfo : summary.bridgeInfo;
        return rows?.length ? toRouteSection(rows, key, label) : null;
      })
      .filter((section): section is RouteSection => section !== null);
  }, [summary]);

  if (sections.length === 0) return null;

  const first = sections[0];
  const last = sections[sections.length - 1];
  const sendToken =
    first.inRow?.valueToken ??
    (from?.symbol ? { symbol: from.symbol, iconUrl: from.iconUrl } : undefined);
  const sendAmount = first.inRow?.value ?? from?.amount;
  const receiveToken =
    last.outRow?.valueToken ??
    (to?.symbol ? { symbol: to.symbol, iconUrl: to.iconUrl } : undefined);
  const receiveAmount = last.outRow?.value ?? to?.amount;

  // 보내는 금액조차 없으면(입력 전) Info 자체가 필요 없다.
  if (!sendAmount) return null;

  const txFeeInfo = summary.txFeeInfo;
  const gasMeta: RouteStepMeta[] =
    includeTxFee && txFeeInfo && !txFeeInfo.isDelegateFee
      ? [
          {
            text: `Gas ${txFeeInfo.estTxFee}${
              txFeeInfo.tokenSymbol ? ` ${txFeeInfo.tokenSymbol}` : ""
            }`,
          },
        ]
      : [];

  const multiHop = sections.length > 1;
  const steps: RouteStep[] = [
    {
      key: "send",
      title: `Send ${sendToken?.symbol ?? ""}`.trim(),
      amount: sendAmount,
      amountToken: sendToken,
      routeTokens: multiHop ? undefined : first.routeTokens,
      meta: [
        // 가스를 이 단계에 붙일 땐 체인 대신 "EST." 를 앞세운다 — 옆에 붙는
        // 금액이 실제 청구액이 아니라 추정치라는 걸 라벨이 먼저 말하게 한다.
        ...(gasMeta.length
          ? [{ text: "EST." }]
          : from?.chain
            ? [{ text: `on ${from.chain}` }]
            : []),
        ...gasMeta,
        ...(multiHop
          ? []
          : [
              ...(first.routeText ? [{ text: first.routeText }] : []),
              ...first.meta.filter((item) => !IMPACT_ROW.test(item.text)),
            ]),
      ],
      // 구간이 여럿이면 대납 태그는 수수료가 붙는 swap 구간에 단다(아래).
      tags: feeDelegated && !multiHop ? ["Fee delegated"] : [],
    },
    ...(multiHop
      ? sections.map((section) => ({
          key: `hop-${section.key}`,
          title: section.label,
          amount: section.routeText ?? "",
          routeTokens: section.routeTokens,
          meta: section.meta,
          tags:
            feeDelegated && section.key === "swap" ? ["Fee delegated"] : [],
        }))
      : []),
    {
      key: "receive",
      title: `Receive ${receiveToken?.symbol ?? ""}`.trim(),
      // 견적이 오기 전에는 값만 비운다. 예전엔 이 사이에 기존 평면 목록으로
      // 폴백해서, 숫자를 입력하면 옛 화면이 한 번 스쳤다.
      amount: receiveAmount ?? "—",
      amountToken: receiveToken,
      meta: [
        ...(to?.chain ? [{ text: `on ${to.chain}` }] : []),
        ...(multiHop
          ? []
          : first.meta.filter((item) => IMPACT_ROW.test(item.text))),
      ],
      tags: [],
    },
  ];

  const rest = [
    ...sections.flatMap((section) => section.rest),
    // Gas Price / Gas Limit 같은 세부 행은 단계 아래 목록에 남긴다.
    ...(includeTxFee ? (summary.txFee ?? []) : []),
  ].map(withImpactTone);
  const showRate = Boolean(rateLine);

  return (
    <>
      <RouteStepList steps={steps} />
      {(rest.length > 0 || showRate) && (
        <div className="bf-info-section">
          <InfoRows
            rows={[
              ...(showRate ? [{ label: "Rate", value: rateLine as string }] : []),
              ...rest,
            ]}
          />
        </div>
      )}
    </>
  );
}

function EmbeddedBridgeDetails({
  summary,
  rateLine,
  from,
  to,
  feeDelegated,
}: {
  summary: BridgeTxSummary;
  rateLine?: string;
  from?: RouteEndpoint;
  to?: RouteEndpoint;
  feeDelegated?: boolean;
}) {
  const normalized = React.useMemo(
    () => normalizeEmbeddedSummary(summary),
    [summary],
  );
  if (!normalized.bridgeInfo?.length && !normalized.swapInfo?.length) return null;
  return (
    <div className="bf-embedded-details">
      <RouteStepSections
        summary={normalized}
        rateLine={rateLine}
        from={from}
        to={to}
        feeDelegated={feeDelegated}
        includeTxFee
      />
    </div>
  );
}

function EmbeddedFlowOverlays({
  flow,
  env,
  toastError,
}: {
  flow: BridgeFlowState;
  env: Environment;
  toastError: string;
}) {
  return (
    <>
      <BridgeTermsModal flow={flow} env={env} />
      <BridgeApproveModal flow={flow} approval={flow.approvalInfo ?? null} />
      <BridgeApprovalSuccessModal flow={flow} />
      <BridgeStatusToast flow={flow} />
      <BridgeErrorToast message={flow.submitToast ? "" : toastError} />
      <BridgeSuccessModal flow={flow} />
    </>
  );
}

function EmbeddedFormView({
  flow,
  env,
  isConnected,
  onRequestConnect,
  feeDelegated,
}: {
  flow: BridgeFlowState;
  env: Environment;
  isConnected: boolean;
  onRequestConnect?: () => void;
  feeDelegated?: boolean;
}) {
  const hasAmount = Number(flow.fromAmount) > 0;
  // `defaultSummary` 는 호출마다 새 객체를 만든다 — 그대로 두면 아래 상세
  // 컴포넌트의 useMemo 가 매 렌더 무효화돼서 memo 를 둔 의미가 없어진다.
  const summary = React.useMemo(
    () => (hasAmount ? flow.summary ?? defaultSummary(flow) : null),
    [hasAmount, flow],
  );
  const fieldErrors = getQuoteFieldErrors(flow.quoteError);
  const insufficientBalance = exceedsBalance(flow.fromAmount, flow.fromToken);
  const fromError = insufficientBalance ? "Insufficient balance" : fieldErrors.from;
  const insufficientLiquidity = /insufficient liquidity|not enough liquidity/i.test(
    flow.quoteError,
  );
  const toError = insufficientLiquidity ? undefined : fieldErrors.to;
  const commonQuoteError =
    flow.quoteError && !fieldErrors.from && !fieldErrors.to
      ? flow.quoteError
      : "";
  const toastError = flow.error || commonQuoteError;
  const isSubmitting = flow.status === "submitting";
  const isDisabled =
    !hasAmount ||
    !flow.toAmount ||
    !flow.fromToken ||
    !flow.toToken ||
    !flow.canSubmit ||
    Boolean(flow.quoteError) ||
    insufficientBalance ||
    isSubmitting;

  const actionLabel = isSubmitting
    ? ""
    : insufficientLiquidity && hasAmount
      ? "Insufficient Liquidity"
      : !hasAmount
        ? "Enter Amount"
        : "Continue";

  return (
    <>
      <main className="bf-body bf-embedded-body" data-rd-scroll-area>
        <div className="bf-embedded-form">
          <div className="bf-embedded-fields">
            <div className="bf-embedded-field-group">
              <EmbeddedAmountPanel
                label="From"
                token={flow.fromToken}
                amount={flow.fromAmount}
                onAmountChange={(value) => flow.setAmount(value, "from")}
                onTokenClick={() => {
                  const next = nextToken(flow.tokens, flow.fromToken);
                  if (next) flow.selectFromToken(next);
                }}
                onMax={flow.setMax}
                locked={flow.tokens.length <= 1}
              />
              {fromError && <div className="bf-embedded-field-error">{fromError}</div>}
            </div>

            <div className="bf-embedded-direction" aria-hidden="true">
              <span><ArrowDownIcon size={16} /></span>
            </div>

            <div className="bf-embedded-field-group">
              <EmbeddedAmountPanel
                label="To"
                token={flow.toToken}
                amount={flow.toAmount}
                onAmountChange={(value) => flow.setAmount(value, "to")}
                onTokenClick={() => {
                  const next = nextToken(flow.availableToTokens, flow.toToken);
                  if (next) flow.selectToToken(next);
                }}
                locked={flow.availableToTokens.length <= 1}
              />
              {toError && <div className="bf-embedded-field-error">{toError}</div>}
            </div>
          </div>

          <Collapse open={Boolean(summary?.liquidity)}>
            {summary?.liquidity && (
              <section className="bf-embedded-liquidity">
                <div>
                  <span>{summary.liquidity.label ?? "Available Liquidity"}</span>
                  <span data-status={summary.liquidity.status}>
                    {summary.liquidity.status ?? "normal"}
                  </span>
                </div>
                <span className="bf-embedded-liquidity-track">
                  <span style={{ width: `${summary.liquidity.percentage ?? 50}%` }} />
                </span>
              </section>
            )}
          </Collapse>

          <Collapse open={insufficientLiquidity && hasAmount}>
            <div className="bf-embedded-liquidity-error" role="alert">
              Insufficient liquidity in all pools for this pair and amount.
            </div>
          </Collapse>

          <Collapse open={Boolean(summary)}>
            {summary && (
            <div className="bf-embedded-summary">
              <EmbeddedBridgeDetails
                summary={summary}
                feeDelegated={feeDelegated}
                rateLine={formatRateLine(
                  flow.fromAmount,
                  flow.toAmount,
                  flow.fromToken,
                  flow.toToken,
                )}
                from={{
                  symbol: flow.fromToken?.symbol,
                  iconUrl: flow.fromToken?.iconUrl,
                  amount: formatAmountForStep(flow.fromAmount),
                  chain: flow.fromToken
                    ? chainLabel(flow.fromToken.chainId)
                    : undefined,
                }}
                to={{
                  symbol: flow.toToken?.symbol,
                  iconUrl: flow.toToken?.iconUrl,
                  amount: formatAmountForStep(flow.toAmount),
                  chain: flow.toToken
                    ? chainLabel(flow.toToken.chainId)
                    : undefined,
                }}
              />
            </div>
            )}
          </Collapse>

          <div className="bf-embedded-action-footer">
            {isConnected ? (
              <button
                type="button"
                className="bf-embedded-cta"
                onClick={flow.submit}
                data-track="next"
                disabled={isDisabled}
                aria-busy={isSubmitting}
                aria-label={
                  isSubmitting
                    ? "Processing transaction"
                    : insufficientLiquidity
                      ? "Insufficient liquidity"
                      : "Continue with swap or bridge"
                }
              >
                {isSubmitting ? (
                  <span className="bf-embedded-spinner" aria-hidden="true" />
                ) : (
                  actionLabel
                )}
              </button>
            ) : (
              <button
                type="button"
                className="bf-embedded-cta"
                onClick={onRequestConnect}
                data-track="connect_click"
                aria-label="Connect wallet to continue"
              >
                Connect Wallet
              </button>
            )}
          </div>
        </div>
      </main>
      <EmbeddedFlowOverlays
        flow={flow}
        env={env}
        toastError={toastError}
      />
    </>
  );
}

function HistoryCard({ item }: { item: BridgeHistoryItem }) {
  const status = getHistoryStatus(item);
  const fiatAmount = formatHistoryUsd(item.fromAmount, item.fromToken);
  return (
    <article className="bf-history-item">
      <div className="bf-history-item-head">
        <span className="bf-history-status" data-status={status}>
          {historyStatusLabel(status)}
        </span>
        <time className="bf-history-time" dateTime={item.timestamp}>
          {formatHistoryTimestamp(item.timestamp)}
        </time>
      </div>
      <div className="bf-history-item-body">
        <strong className="bf-history-amount">
          {formatHistoryTokenAmount(item.fromAmount)} {item.fromToken.symbol}
        </strong>
        <div className="bf-history-meta">
          <span className="bf-history-usd">{fiatAmount ?? shortHash(item.txHash)}</span>
          <span className="bf-history-route">
            <span className="bf-history-route-token">
              <TokenIcon token={item.fromToken} size={16} />
              <span>{item.fromToken.symbol}</span>
            </span>
            <ArrowRightIcon size={16} className="bf-history-route-arrow" />
            <span className="bf-history-route-token">
              <TokenIcon token={item.toToken} size={16} />
              <span>{item.toToken.symbol}</span>
            </span>
          </span>
        </div>
      </div>
    </article>
  );
}

function HistoryView({ flow }: { flow: BridgeFlowState }) {
  const [filter, setFilter] = React.useState<HistoryFilter>("all");
  const hasPending = flow.history.some((item) => getHistoryStatus(item) === "pending");
  const visibleHistory = flow.history.filter((item) => historyMatchesFilter(item, filter));
  const emptyMessage =
    filter === "all"
      ? "No bridge history yet."
      : `No ${filter} bridge history.`;

  return (
    <main className="bf-history-body">
      <nav className="bf-history-tabs" aria-label="Bridge history filters">
        {HISTORY_FILTERS.map((item) => (
          <button
            key={item.value}
            type="button"
            className="bf-history-tab"
            data-active={filter === item.value ? "true" : "false"}
            onClick={() => setFilter(item.value)}
            data-track="history_filter"
            data-track-id={item.value}
          >
            <span>{item.label}</span>
            {item.value === "pending" && hasPending && (
              <span className="bf-history-tab-dot" aria-hidden="true" />
            )}
          </button>
        ))}
      </nav>
      {visibleHistory.length > 0 ? (
        <div className="bf-history-list">
          {visibleHistory.map((item) => (
            <HistoryCard key={item.id || item.txHash} item={item} />
          ))}
        </div>
      ) : (
        <div className="bf-empty">{emptyMessage}</div>
      )}
    </main>
  );
}

function DefaultFormView({
  flow,
  env,
  feeDelegated,
}: {
  flow: BridgeFlowState;
  env: Environment;
  feeDelegated?: boolean;
}) {
  const [slippageSettingsOpen, setSlippageSettingsOpen] = React.useState(false);
  const [swapRotation, setSwapRotation] = React.useState(0);
  const hasAmount = Boolean(flow.fromAmount.trim() || flow.toAmount.trim());
  // `defaultSummary` 는 호출마다 새 객체를 만든다 — 그대로 두면 아래 상세
  // 컴포넌트의 useMemo 가 매 렌더 무효화돼서 memo 를 둔 의미가 없어진다.
  const summary = React.useMemo(
    () => (hasAmount ? flow.summary ?? defaultSummary(flow) : null),
    [hasAmount, flow],
  );
  // apps.json(bridgePoweredBy.url)이 단일 소스. 로드 전에는 링크 미표시.
  const poweredByUrl = useRemoteConfigValue<AppsConfig, string>(
    "apps.json",
    env,
    (config) => resolveEnvValue(config?.bridgePoweredBy?.url, env),
  );
  const fieldErrors = getQuoteFieldErrors(flow.quoteError);
  const insufficientBalance = exceedsBalance(flow.fromAmount, flow.fromToken);
  const fromError = insufficientBalance ? "Insufficient balance" : fieldErrors.from;
  const commonQuoteError =
    flow.quoteError && !fieldErrors.from && !fieldErrors.to
      ? flow.quoteError
      : "";
  const toastError = flow.error || commonQuoteError;
  // Show the dot whenever history has a pending tx (persists across reloads),
  // or a freshly-submitted tx is awaiting its first view.
  const hasPendingHistory = flow.history.some(
    (item) => getHistoryStatus(item) === "pending",
  );
  // Slippage only applies to swap-bearing routes. A pure cross-chain bridge of
  // the same asset (e.g. CROSS↔CROSS, USDT↔USDT) has no swap, so hide it there.
  const slippagePathType =
    summary?.pathType ?? inferPathType(flow.fromToken, flow.toToken);
  const showSlippage = slippagePathType !== "bridge";

  return (
    <>
      <main className="bf-body">
        <div className="bf-toolbar">
          <button
            type="button"
            className="bf-chip bf-chip-history"
            onClick={() => flow.setStep("history")}
            data-track="history_open"
          >
            <HistoryIcon />
            <span>History</span>
            {(hasPendingHistory || flow.hasNewHistory) && (
              <span className="bf-chip-dot" aria-hidden="true" />
            )}
          </button>
          {showSlippage && (
            <button
              type="button"
              className="bf-chip"
              onClick={() => setSlippageSettingsOpen(true)}
              data-track="slippage_open"
              disabled={flow.slippageLocked}
              aria-haspopup="dialog"
              aria-expanded={slippageSettingsOpen}
            >
              <SlidersIcon />
              <span>
                {flow.slippageLocked
                  ? "Auto 1%"
                  : formatSlippageLabel(flow.slippage, flow.effectiveSlippage)}
              </span>
            </button>
          )}
        </div>

        <div
          className="bf-swap-card"
          data-has-from-error={fromError ? "true" : "false"}
        >
          <BridgeAmountPanel
            label="From"
            token={flow.fromToken}
            amount={flow.fromAmount}
            usd={formatUsd(flow.fromAmount, flow.fromToken)}
            onAmountChange={(value) => flow.setAmount(value, "from")}
            onTokenClick={() => {
              const next = nextToken(flow.tokens, flow.fromToken);
              if (next) flow.selectFromToken(next);
            }}
            locked={flow.tokens.length <= 1}
          />
          {fromError && (
            <div className="bf-field-error">{fromError}</div>
          )}
          <button
            type="button"
            className="bf-swap-toggle"
            onClick={() => { flow.swapTokens(); setSwapRotation((r) => r + 360); }}
            data-track="token_swap"
            style={{ transform: `translateX(-50%) rotate(${swapRotation}deg)`, transition: "transform 0.2s ease" }}
          >
            <ArrowDownIcon size={22} />
          </button>
          <BridgeAmountPanel
            label="To"
            token={flow.toToken}
            amount={flow.toAmount}
            usd={formatUsd(flow.toAmount, flow.toToken)}
            onAmountChange={(value) => flow.setAmount(value, "to")}
            onTokenClick={() => {
              const next = nextToken(flow.availableToTokens, flow.toToken);
              if (next) flow.selectToToken(next);
            }}
            locked={flow.availableToTokens.length <= 1}
          />
          {fieldErrors.to && (
            <div className="bf-field-error">{fieldErrors.to}</div>
          )}
        </div>

        <ExchangeRate
          fromAmount={flow.fromAmount}
          toAmount={flow.toAmount}
          fromToken={flow.fromToken}
          toToken={flow.toToken}
        />

        <Collapse open={Boolean(summary)}>
          {summary && (
            <BridgeDetails
              summary={summary}
              feeDelegated={feeDelegated}
              from={{
                symbol: flow.fromToken?.symbol,
                iconUrl: flow.fromToken?.iconUrl,
                amount: formatAmountForStep(flow.fromAmount),
                chain: flow.fromToken
                  ? chainLabel(flow.fromToken.chainId)
                  : undefined,
              }}
              to={{
                symbol: flow.toToken?.symbol,
                iconUrl: flow.toToken?.iconUrl,
                amount: formatAmountForStep(flow.toAmount),
                chain: flow.toToken ? chainLabel(flow.toToken.chainId) : undefined,
              }}
            />
          )}
        </Collapse>
      </main>
      <footer className="bf-footer">
        <button
          type="button"
          className="bf-next"
          onClick={flow.submit}
          data-track="next"
          disabled={flow.status === "submitting" || Boolean(flow.quoteError) || insufficientBalance}
        >
          {flow.status === "submitting" ? "Processing..." : "Next"}
        </button>
        {poweredByUrl && (
          <a
            className="bf-powered"
            href={poweredByUrl}
            target="_blank"
            rel="noreferrer"
          >
            Powered by <span className="bf-powered-brand">CROSS DeFi</span>
          </a>
        )}
      </footer>
      {slippageSettingsOpen && (
        <BridgeSlippageSettings
          flow={flow}
          onClose={() => setSlippageSettingsOpen(false)}
        />
      )}
      <BridgeTermsModal flow={flow} env={env} />
      <BridgeApproveModal flow={flow} approval={flow.approvalInfo ?? null} />
      <BridgeApprovalSuccessModal flow={flow} />
      <BridgeStatusToast flow={flow} />
      <BridgeErrorToast message={flow.submitToast ? "" : toastError} />
      <BridgeSuccessModal flow={flow} />
    </>
  );
}

export function BridgeFlow({
  onClose,
  onBackToWallet,
  feeDelegated,
  env,
  variant = "default",
  title: titleProp,
  isConnected: isConnectedProp,
  onRequestConnect,
  className,
  ...rest
}: BridgeFlowProps) {
  const resolvedEnv = React.useMemo(() => resolveEnvironment(env), [env]);
  const isSoftKeyboardOpen = useSoftKeyboardOpen();
  const flow = useBridgeFlow({
    ...rest,
    onExit: onBackToWallet,
  });

  const rootRef = React.useRef<HTMLDivElement>(null);
  const theme = useDetectTheme(rootRef);
  const title = flow.step === "history" ? "History" : titleProp ?? "Swap & Bridge";
  const isConnected = isConnectedProp ?? Boolean(rest.walletAddress);
  const backAction =
    variant === "embedded" && flow.step === "form" && !onBackToWallet
      ? null
      : flow.back;

  return (
    <TrackingBoundary feature="bridge">
    <ThemeContext.Provider value={theme}>
    <div
      ref={rootRef}
      className={cn("bf-root", className)}
      data-view={flow.step}
      data-status={flow.status}
      data-variant={variant}
      data-soft-keyboard-open={
        variant === "embedded" && isSoftKeyboardOpen ? "true" : "false"
      }
    >
      <header className="bf-header">
        {/* Embedded form has nowhere to go back to unless the host gives us an
            exit (e.g. Get ONEUSD returns to its action chooser). */}
        {!backAction ? (
          <span className="bf-header-spacer" aria-hidden="true" />
        ) : (
          <button type="button" className="bf-header-btn" onClick={backAction} aria-label="Back">
            <BackIcon size={24} />
          </button>
        )}
        {/* Bigger hit target for the back arrow. Keyboard/a11y stays on the
            button, so the title stays a plain span. */}
        <span
          className="bf-header-title"
          data-clickable={backAction ? "true" : "false"}
          onClick={backAction ?? undefined}
        >
          {title}
        </span>
        <button type="button" className="bf-header-btn" onClick={onClose} aria-label="Close">
          <CloseIcon size={24} />
        </button>
      </header>

      {flow.step === "history" ? (
        <HistoryView flow={flow} />
      ) : variant === "embedded" ? (
        <EmbeddedFormView
          flow={flow}
          env={resolvedEnv}
          isConnected={isConnected}
          onRequestConnect={onRequestConnect}
          feeDelegated={feeDelegated}
        />
      ) : (
        <DefaultFormView
          flow={flow}
          env={resolvedEnv}
          feeDelegated={feeDelegated}
        />
      )}
    </div>
    </ThemeContext.Provider>
    </TrackingBoundary>
  );
}
